/**
 * Establishments dashboard section: list with create/edit collapse.
 */

import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { EstablishmentsDataTable } from '@/components/establishments/EstablishmentsDataTable';
import { CreateEstablishmentView } from '@/components/establishments/CreateEstablishmentView';
import { EditEstablishmentView } from '@/components/establishments/EditEstablishmentView';
import { CollapseReplaceWrapper } from '@/components/common/CollapseReplaceWrapper';
import { useDetailsView } from '@/hooks/useDetailsView';
import { usePageActions } from '@/hooks/usePageActions';
import type { Establishment } from '@/types/establishment.types';
import type { ElementWidth } from '@/types/interface-elements.types';

/** Which form is mounted in the collapse wrapper. */
type ActiveForm = 'create' | 'edit' | null;

/** Props for {@link EstablishmentsSection}. */
interface EstablishmentsSectionProps {
  layoutWidth?: ElementWidth;
  onSuccess?: () => void;
}

/** Dashboard widget: establishments list with create/edit. */
export const EstablishmentsSection: React.FC<EstablishmentsSectionProps> = ({
  layoutWidth = 'full',
  onSuccess,
}) => {
  const [activeForm, setActiveForm] = React.useState<ActiveForm>(null);
  const [editEstablishmentId, setEditEstablishmentId] = React.useState<string | null>(null);
  const { openRowId, openRow, closeRow } = useDetailsView();
  const sectionRef = React.useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();
  const { canExportCsv } = usePageActions('establishments');

  /** Close the form and drop the edit id so the next open starts clean. */
  const closeForm = React.useCallback(() => {
    setActiveForm(null);
    setEditEstablishmentId(null);
  }, []);

  /** Close the form and refresh the establishments list. */
  const handleSuccess = React.useCallback(() => {
    setActiveForm(null);
    setEditEstablishmentId(null);
    void queryClient.invalidateQueries({ queryKey: ['establishments'] });
    onSuccess?.();
  }, [queryClient, onSuccess]);

  const handleEditEstablishment = (establishment: Establishment) => {
    setEditEstablishmentId(establishment.id);
    setActiveForm('edit');
    closeRow();
  };

  /** Scroll the section to the form when it first opens. */
  const prevActiveFormRef = React.useRef(activeForm);
  React.useEffect(() => {
    if (prevActiveFormRef.current !== activeForm && activeForm !== null && sectionRef.current) {
      sectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    prevActiveFormRef.current = activeForm;
  }, [activeForm]);

  const formContent = React.useMemo(() => {
    if (activeForm === 'create') {
      return <CreateEstablishmentView onClose={closeForm} onSuccess={handleSuccess} />;
    }
    if (activeForm === 'edit' && editEstablishmentId) {
      return (
        <EditEstablishmentView
          key={editEstablishmentId}
          establishmentId={editEstablishmentId}
          onClose={closeForm}
          onSuccess={handleSuccess}
        />
      );
    }
    return null;
  }, [activeForm, editEstablishmentId, closeForm, handleSuccess]);

  return (
    <div ref={sectionRef}>
      <CollapseReplaceWrapper mode="replace" isOpen={activeForm !== null} formContent={formContent}>
        <EstablishmentsDataTable
          showCreateButton={true}
          layoutWidth={layoutWidth}
          isSection={true}
          expandedRowId={openRowId}
          onToggleRow={openRow}
          onCloseDetails={closeRow}
          onEditEstablishment={handleEditEstablishment}
          onCreateClick={() => {
            setActiveForm('create');
            setEditEstablishmentId(null);
            closeRow();
          }}
          pageActions={{ canExportCsv }}
        />
      </CollapseReplaceWrapper>
    </div>
  );
};
